import { atom, DefaultValue, selector } from "recoil"
import { nativeMobileSafeAreaAtom } from "../nativeMobileSafeArea"

const fullScreen = atom({
  key: "fullScreen",
  default: false
})

const firstLoadChat = atom({
  key: "firstLoadChat",
  default: true
})

const showHeader = atom({
  key: "showHeader",
  default: false
})

const openSidebar = atom({
  key: "openSidebar",
  default: true
})

const gridViewEnabled = atom({
  key: "gridViewEnabled",
  default: false
})

const historyTabEnabled = atom({
  key: "historyTabEnabled",
  default: 0
})

const historyTabEnabledMobile = atom({
  key: "historyTabEnabledMobile",
  default: 0
})

const chatMessageHeight = atom({
  key: "chatMessageHeight",
  default: null
})

const recordingView = atom({
  key: "recordingView",
  default: false
})

const isChatTab = atom({
  key: "isChatTab",
  default: false
})

const isBadgesListLive = atom({
  key: "isBadgesListLive",
  default: false
})

const isTransitionFromDesktopVersion = atom({
  key: "isTransitionFromDesktopVersion",
  default: false
})

const isOneTabBadgesHistory = atom({
  key: "isOneTabBadgesHistory",
  default: false
})

const isOneTab = atom({
  key: "isOneTab",
  default: false
})

const fsInterfaceTab = atom({
  key: "fsInterfaceTab",
  default: "chat"
})

const isOpenMobileMainInput = atom({
  key: "isOpenMobileMainInput",
  default: false
})

const hideLeaveButton = atom({
  key: "hideLeaveButton",
  default: false
})

const keepLeaveOverlayActive = atom({
  key: "keepLeaveOverlayActive",
  default: false
})

const hideFSScrollControlChat = atom({
  key: "hideFSScrollControlChat",
  default: false
})

const hideFSScrollControlBadges = atom({
  key: "hideFSScrollControlBadges",
  default: false
})

const hideFSScrollControlVideos = atom({
  key: "hideFSScrollControlVideosAtom",
  default: false
})

const firstRenderMobile = atom({
  key: "firstRenderMobileAtom",
  default: false
})

// showOnVideoOverlayContent and visibleMobileFSTabs refer to the hide/show fullscreen on video content. TODO: remove one of these properties.
const showOnVideoOverlayContent = atom({
  key: "showOnVideoOverlayContent",
  default: false // hide the content for initial load 
})

const visibleMobileFSTabs = atom<boolean>({
  key: "visibleMobileFSTabs",
  default: true
})

const isSidePanelWithSingleTab = atom<boolean>({
  key: "isSidePanelWithSingleTab",
  default: false
})

const showJoinAgainButtonOnByePage = atom<boolean>({
  key: "showJoinAgainButtonOnByePage",
  default: true
})

// TODO: Change to isMiniPlayerActive
const isFloatingDivActive = atom<boolean>({
  key: "isFloatingDivActive",
  default: false
})

// Atom to store the current screen width and height
const screenSizeAtom = atom({
  key: "screenSizeAtom",
  default: {
    width: window.innerWidth,
    height: window.innerHeight
  }
})

const screenSizeSelector = selector<{
  width: number,
  height: number
}>({
  key: "screenSizeSelector",
  get({ get }) {
    return get(screenSizeAtom)
  },
  set({ get, set }, newValue) {

    if(newValue instanceof DefaultValue) return
    // calculate the safe area height, by minus status bar and bottom bar in native app
    const { bottom, top } =  get(nativeMobileSafeAreaAtom)
    const height = newValue.height - bottom - top

    set(screenSizeAtom, {
      width: newValue.width,
      height: height
    })
  }
})

const isMobileDevice = atom({
  key: "isMobileDevice",
  default: false
})

const isTabletDevice = atom({
  key: "isTabletDevice",
  default: false
})

const overlayDisplayAllowed = atom({
  key: "overlayDisplayAllowed",
  default: true
})

export type Tabs = "chat" | "watch" | "shop" | "closed" | "control" | "inspirations"

const activeTab = atom<Tabs>({
  key: "activeTab",
  default: "chat"
})

const previousTab = atom<Tabs>({
  key: "previousTab",
  default: "chat"
})

export const changeTabSelector = selector<Tabs>({
  key: "changeTabSelector",
  get: ({ get }) => {
    return get(activeTab)
  },
  set: ({ set, get }, newTab) => {
    if (newTab === "closed") {
      set(visibleMobileFSTabs, false)
      set(showOnVideoOverlayContent, false)
    } else {
      set(visibleMobileFSTabs, true)
      set(showOnVideoOverlayContent, true)
    }
    const curTab = get(activeTab)
    set(previousTab, curTab)
    set(activeTab, newTab)
  }
})

const showLeaveOverlay = atom<boolean> ({
  key: "showLeaveOverlay",
  default: true
})

const layoutStates = {
  isMobileDevice,
  isTabletDevice,
  chatMessageHeight,
  fullScreen,
  showHeader,
  openSidebar,
  gridViewEnabled,
  historyTabEnabled,
  recordingView,
  isChatTab,
  firstLoadChat,
  isBadgesListLive,
  isTransitionFromDesktopVersion,
  isOneTabBadgesHistory,
  isOneTab,
  isSidePanelWithSingleTab,
  fsInterfaceTab,
  isOpenMobileMainInput,
  historyTabEnabledMobile,
  hideLeaveButton,
  hideFSScrollControlChat,
  hideFSScrollControlBadges,
  firstRenderMobile,
  showOnVideoOverlayContent,
  visibleMobileFSTabs,
  hideFSScrollControlVideos,
  showJoinAgainButtonOnByePage,
  keepLeaveOverlayActive,
  isFloatingDivActive,
  screenSizeSelector,
  overlayDisplayAllowed,
  activeTab,
  previousTab,
  showLeaveOverlay
}

export default layoutStates
